import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Breadcrumb, Layout } from 'antd';

import { articleContentType } from 'common/constants/content';
import { QUERY_GET_ARTICLE_DETAILS } from './queries';

const { Content } = Layout;

const useStyles = makeStyles(theme => ({
  wrap: {
    background: theme.colors.gray1,
  },
  breadCrumbType: {
    '&>span:hover': { color: theme.colors.mainGreen5 },
  },
  box: {
    width: 786,
    margin: 'auto',
    marginBottom: 56,
  },
  detail: {
    padding: '40 0 80 175',
  },
  title: {
    ...theme.typography.h2,
    color: theme.colors.titleColor,
    marginBottom: 20,
  },
  subTitle: {
    ...theme.typography.h5,
    color: theme.colors.bodyColor,
    marginBottom: 16,
  },
  textArea: {
    ...theme.typography.h5,
    fontWeight: 400,
    color: theme.colors.bodyColor,
    marginBottom: 16,
  },
  breadCrumb: {
    marginTop: 24,
    marginBottom: 32,
    '& span': {
      ...theme.typography.body4,
      color: theme.colors.captionColor,
      fontWeight: 400,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    '& .ant-breadcrumb a:hover': {
      color: theme.colors.captionColor,
    },
  },
  breadCrumbDetail: {
    '&>span': {
      color: theme.colors.titleColor,
      textTransform: 'capitalize',
      fontSize: 12,
    },
    '& span:hover': {
      cursor: 'text',
    },
  },
  imgWrap: {
    marginBottom: 16,
  },
  imgBox: {
    width: 786,
    background: theme.colors.gray2,
    '&>img': {
      width: '100%',
    },
  },
  videoPlayer: {
    position: 'relative',
    width: 786,
    height: 440,

    '& > video': {
      background: '#000',
    },
  },
  videoHeader: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    height: 96,
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.colors.gray1,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0) 100%)',

    '& > span': {
      fontSize: 20,
      lineHeight: '32px',
      textShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)',
    },

    '& .ant-btn': {
      color: theme.colors.gray1,
      textAlign: 'center',
    },
  },
}));

const ArticleDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [detail, setDetail] = useState(null);
  const [showVideoHeader, setShowVideoHeader] = useState(false);

  const timerRef = useRef(null);

  const [queryDetail] = useLazyQuery(QUERY_GET_ARTICLE_DETAILS, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      if (res?.article?.article) {
        setDetail(res?.article?.article);
      }
    },
  });

  const goHelpCenter = () => {
    history.push('/new-help-center');
  };

  const handleVideoMoveIn = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setShowVideoHeader(true);
  };

  const handleVideoMoveOut = () => {
    timerRef.current = setTimeout(() => {
      setShowVideoHeader(false);
    }, 3000);
  };

  useEffect(() => {
    queryDetail({ variables: { id } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Layout>
      <Content className={classes.wrap}>
        <div className={classes.detail}>
          <div className={classes.box}>
            <div className={classes.breadCrumb}>
              <Breadcrumb>
                <Breadcrumb.Item className={classes.breadCrumbType}>
                  <span onClick={goHelpCenter}>Home</span>
                </Breadcrumb.Item>
                <Breadcrumb.Item className={classes.breadCrumbType}>
                  <span onClick={goHelpCenter} style={{ textTransform: 'capitalize' }}>
                    {detail?.type?.toLowerCase()}
                  </span>
                </Breadcrumb.Item>
                <Breadcrumb.Item className={classes.breadCrumbDetail}>
                  <span>{detail?.title}</span>
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <If condition={!!detail?.articleData?.length}>
              <div className={classes.contents}>
                <For each="item" of={detail?.articleData} index="index">
                  <If condition={item?.type === articleContentType.TITLE}>
                    <div className={classes.title}>{item?.content}</div>
                  </If>
                  <If condition={item?.type === articleContentType.TEXTAREA}>
                    <div className={classes.textArea}>{item?.content}</div>
                  </If>
                  <If condition={item?.type === articleContentType.SUBTITLE}>
                    <div className={classes.subTitle}>{item?.content}</div>
                  </If>
                  <If condition={item?.type === articleContentType.IMAGE}>
                    <div className={classes.imgWrap}>
                      <div className={classes.imgBox}>
                        <img
                          src={item?.content}
                          alt={t('landlord.new_dashboard.counter-offer-help.title')}
                        />
                      </div>
                    </div>
                  </If>
                  <If condition={item?.type === articleContentType.VIDEO}>
                    <div
                      className={classes.videoPlayer}
                      onMouseEnter={handleVideoMoveIn}
                      onMouseOut={handleVideoMoveOut}
                    >
                      <video width={'100%'} height={'100%'} controls poster={item.links?.thumbnail}>
                        <source src={item.links?.hqMp4} />
                        <source src={item.links?.lqMp4} />
                      </video>
                      <If condition={showVideoHeader}>
                        <div className={classes.videoHeader}>
                          <span>{item.title}</span>
                        </div>
                      </If>
                    </div>
                  </If>
                </For>
              </div>
            </If>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default ArticleDetail;
